import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { X, Trash2, Minus, Plus, ShoppingBag, Loader2 } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { Button } from "@/components/ui/button";
import api from "@/lib/apiClient";
import { toast } from "sonner";

export default function CartDrawer() {
  const { items, remove, setQty, total, open, setOpen, count } = useCart();
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const checkout = async () => {
    setLoading(true);
    try {
      const payload = {
        items: items.map((i) => ({ product_id: i.id, quantity: i.quantity })),
        origin_url: window.location.origin,
      };
      const { data } = await api.post("/payments/checkout", payload);
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error("Could not start checkout. Please try again.");
      setLoading(false);
    }
  };

  return (
    <>
      {open && <div className="fixed inset-0 z-[60] bg-black/40 backdrop-blur-sm" onClick={() => setOpen(false)} />}
      <aside data-testid="cart-drawer"
        className={`fixed top-0 right-0 z-[70] h-full w-full sm:w-[420px] bg-[#FAF9F6] dark:bg-blue-950 shadow-2xl transition-transform duration-300 flex flex-col ${open ? "translate-x-0" : "translate-x-full"}`}>
        <div className="flex items-center justify-between px-5 h-20 border-b border-blue-900/10">
          <h3 className="font-display font-bold text-lg flex items-center gap-2 text-blue-950 dark:text-blue-50">
            <ShoppingBag className="w-5 h-5" /> Your Cart ({count})
          </h3>
          <button onClick={() => setOpen(false)} data-testid="cart-close-button" className="p-2 rounded-lg hover:bg-blue-100 dark:hover:bg-blue-900">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
          {items.length === 0 && (
            <div className="text-center py-20 text-slate-500">
              <ShoppingBag className="w-12 h-12 mx-auto mb-3 opacity-30" />
              <p>Your cart is empty.</p>
              <Button variant="link" className="text-blue-800" onClick={() => { setOpen(false); navigate("/store"); }}>Browse the store</Button>
            </div>
          )}
          {items.map((i) => (
            <div key={i.id} data-testid={`cart-item-${i.id}`} className="flex gap-3 bg-white dark:bg-blue-900/30 rounded-xl p-3 border border-blue-900/5">
              <img src={i.image} alt={i.name} className="w-16 h-16 rounded-lg object-cover" />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium leading-snug line-clamp-2">{i.name}</p>
                <p className="text-sm text-blue-800 dark:text-blue-300 font-semibold mt-0.5">${i.price.toFixed(2)}</p>
                <div className="flex items-center gap-2 mt-1.5">
                  <button onClick={() => setQty(i.id, i.quantity - 1)} className="p-1 rounded border border-blue-900/15"><Minus className="w-3 h-3" /></button>
                  <span className="text-sm w-6 text-center">{i.quantity}</span>
                  <button onClick={() => setQty(i.id, i.quantity + 1)} className="p-1 rounded border border-blue-900/15"><Plus className="w-3 h-3" /></button>
                  <button onClick={() => remove(i.id)} data-testid={`cart-remove-${i.id}`} className="ml-auto p-1 text-red-500 hover:bg-red-50 rounded"><Trash2 className="w-4 h-4" /></button>
                </div>
              </div>
            </div>
          ))}
        </div>

        {items.length > 0 && (
          <div className="border-t border-blue-900/10 px-5 py-4 space-y-3">
            <div className="flex justify-between text-lg font-display font-bold">
              <span>Total</span>
              <span data-testid="cart-total">${total.toFixed(2)}</span>
            </div>
            <p className="text-xs text-slate-500">Instant digital download after secure Stripe checkout.</p>
            <Button data-testid="cart-checkout-button" disabled={loading} onClick={checkout}
              className="w-full h-12 bg-amber-500 hover:bg-amber-600 text-white font-semibold text-base">
              {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : "Secure Checkout"}
            </Button>
          </div>
        )}
      </aside>
    </>
  );
}
